import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, withKeys as _withKeys, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = { class: "d-flex align-center flex-wrap mb-3" };
const _hoisted_2 = {
    key: 0,
    class: "text-caption text-error ml-2"
};
const _hoisted_3 = {
    key: 1,
    class: "d-flex justify-center py-8"
};
const _hoisted_4 = ["aria-pressed", "onClick"];
const _hoisted_5 = { class: "text-caption text-center mt-1" };
import { computed, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useDisplay } from "vuetify";
import * as pdfjs from "pdfjs-dist";
import ChartPagePreviewDialog from "@/components/ChartPagePreviewDialog.vue";
import ChartPageThumbnail from "@/components/ChartPageThumbnail.vue";
/**
 * Thumbnail picker over the best compressed copy of a chart that did not fit the budget. Thumbnails render
 * only as their row scrolls into view, so a 300-page document costs one screen of renders at a time; any
 * page opens at reading size in a nested preview.
 */
const THUMB_WIDTH = 150;
const THUMB_HEIGHT = 194;
const GRID_HEIGHT = 480;
const MB = 1024 * 1024;
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartPagePickerDialog',
    props: {
        modelValue: { type: Boolean },
        downloadUrl: {},
        perPageBytes: {},
        budgetBytes: {}
    },
    emits: ["update:modelValue", "confirm"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const ROW_HEIGHT = THUMB_HEIGHT + 44;
        if (!pdfjs.GlobalWorkerOptions.workerSrc) {
            pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();
        }
        // shallowRef: pdf.js objects carry private fields that break under Vue's reactive Proxy.
        const pdf = shallowRef(null);
        const loadError = ref(null);
        const selected = ref(new Set());
        const rangeInput = ref("");
        const rangeHint = ref(null);
        const previewPage = ref(null);
        let loadSequence = 0;
        const { xs, sm } = useDisplay();
        const pagesPerRow = computed(() => (xs.value ? 2 : sm.value ? 3 : 4));
        const pageCount = computed(() => props.perPageBytes.length);
        const rows = computed(() => {
            const perRow = pagesPerRow.value;
            const result = [];
            for (let page = 1; page <= pageCount.value; page += perRow) {
                result.push(Array.from({ length: Math.min(perRow, pageCount.value - page + 1) }, (_, i) => page + i));
            }
            return result;
        });
        const selectedBytes = computed(() => [...selected.value].reduce((sum, page) => sum + (props.perPageBytes[page - 1] ?? 0), 0));
        const fits = computed(() => selectedBytes.value <= props.budgetBytes);
        const meterPercent = computed(() => Math.min(100, (selectedBytes.value / Math.max(1, props.budgetBytes)) * 100));
        const formatMb = (bytes) => (bytes / MB).toFixed(bytes < MB ? 2 : 1);
        const meterText = computed(() => `${formatMb(selectedBytes.value)} MB of ${formatMb(props.budgetBytes)} MB · ${selected.value.size} of ${pageCount.value} pages`);
        const selectAll = () => {
            selected.value = new Set(Array.from({ length: pageCount.value }, (_, i) => i + 1));
        };
        const clearSelection = () => {
            selected.value = new Set();
        };
        const toggle = (page) => {
            const next = new Set(selected.value);
            if (next.has(page))
                next.delete(page);
            else
                next.add(page);
            selected.value = next;
        };
        const applyRange = () => {
            rangeHint.value = null;
            const next = new Set();
            for (const token of rangeInput.value.split(",")) {
                const match = /^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/.exec(token);
                if (!match)
                    continue;
                const first = Number(match[1]);
                const last = match[2] ? Number(match[2]) : first;
                for (let page = Math.max(1, first); page <= Math.min(pageCount.value, last); page++)
                    next.add(page);
            }
            if (next.size)
                selected.value = next;
            else
                rangeHint.value = "No pages matched. Use page numbers or ranges like 1-40, 55-120.";
        };
        const release = () => {
            loadSequence++;
            pdf.value?.destroy();
            pdf.value = null;
            previewPage.value = null;
        };
        const confirm = () => emit("confirm", [...selected.value].sort((a, b) => a - b));
        const load = async () => {
            release();
            loadError.value = null;
            selectAll();
            rangeInput.value = "";
            rangeHint.value = null;
            const sequence = ++loadSequence;
            try {
                const document = await pdfjs.getDocument({ url: props.downloadUrl }).promise;
                if (sequence !== loadSequence || !props.modelValue) {
                    document.destroy();
                    return;
                }
                pdf.value = document;
            }
            catch {
                if (sequence === loadSequence)
                    loadError.value = "The document preview could not be loaded. Please try again.";
            }
        };
        watch(() => [props.modelValue, props.downloadUrl], ([open]) => {
            if (open)
                load();
            else
                release();
        }, { immediate: true });
        onBeforeUnmount(release);
        return (_ctx, _cache) => {
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_progress_linear = _resolveComponent("v-progress-linear");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_virtual_scroll = _resolveComponent("v-virtual-scroll");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_card_actions = _resolveComponent("v-card-actions");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "980",
                scrollable: "",
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => (emit('update:modelValue', $event)))
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
                                default: _withCtx(() => [
                                    _cache[6] || (_cache[6] = _createTextVNode(" Choose pages ", -1)),
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn, {
                                        icon: "mdi-close",
                                        variant: "text",
                                        size: "small",
                                        onClick: _cache[0] || (_cache[0] = ($event) => (emit('update:modelValue', false)))
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, null, {
                                default: _withCtx(() => [
                                    _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-body-2 text-medium-emphasis mb-3" }, " Keep the pages CoverMyMeds needs and leave the rest out. The meter shows the size of the selected pages against the remaining budget. ", -1)),
                                    _createElementVNode("div", _hoisted_1, [
                                        _createVNode(_component_v_text_field, {
                                            modelValue: rangeInput.value,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((rangeInput).value = $event)),
                                            placeholder: "e.g. 1-40, 55-120",
                                            density: "compact",
                                            "hide-details": "",
                                            class: "mr-2 picker-range",
                                            onKeyup: _withKeys(applyRange, ["enter"])
                                        }, null, 8, ["modelValue"]),
                                        _createVNode(_component_v_btn, {
                                            size: "small",
                                            variant: "tonal",
                                            color: "primary",
                                            class: "mr-2",
                                            onClick: applyRange
                                        }, {
                                            default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                    _createTextVNode("Apply", -1)
                                                ]))]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_btn, {
                                            size: "small",
                                            variant: "text",
                                            class: "mr-2",
                                            onClick: selectAll
                                        }, {
                                            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                                    _createTextVNode("Select all", -1)
                                                ]))]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_btn, {
                                            size: "small",
                                            variant: "text",
                                            onClick: clearSelection
                                        }, {
                                            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                                    _createTextVNode("Clear", -1)
                                                ]))]),
                                            _: 1
                                        }),
                                        (rangeHint.value)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(rangeHint.value), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["text-body-2 mb-1", fits.value ? 'text-success' : 'text-error'])
                                    }, _toDisplayString(meterText.value), 3),
                                    _createVNode(_component_v_progress_linear, {
                                        "model-value": meterPercent.value,
                                        color: fits.value ? 'success' : 'error',
                                        height: "6",
                                        rounded: "",
                                        class: "mb-3"
                                    }, null, 8, ["model-value", "color"]),
                                    (loadError.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 0,
                                            type: "error",
                                            variant: "tonal",
                                            density: "compact"
                                        }, {
                                            default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(loadError.value), 1)
                                            ]),
                                            _: 1
                                        }))
                                        : (!pdf.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                                _createVNode(_component_v_progress_circular, {
                                                    indeterminate: "",
                                                    color: "primary"
                                                })
                                            ]))
                                            : (_openBlock(), _createBlock(_component_v_virtual_scroll, {
                                                key: 2,
                                                items: rows.value,
                                                height: GRID_HEIGHT,
                                                "item-height": ROW_HEIGHT
                                            }, {
                                                default: _withCtx(({ item: row }) => [
                                                    _createVNode(_component_v_row, { "no-gutters": "" }, {
                                                        default: _withCtx(() => [
                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (page) => {
                                                                return (_openBlock(), _createBlock(_component_v_col, {
                                                                    key: page,
                                                                    cols: 12 / pagesPerRow.value,
                                                                    class: "picker-cell pa-1"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createElementVNode("button", {
                                                                            type: "button",
                                                                            class: _normalizeClass(["picker-page pa-1 rounded", { 'picker-page--selected': selected.value.has(page) }]),
                                                                            "aria-pressed": selected.value.has(page),
                                                                            onClick: ($event) => (toggle(page))
                                                                        }, [
                                                                            _createVNode(ChartPageThumbnail, {
                                                                                pdf: pdf.value,
                                                                                page: page,
                                                                                width: THUMB_WIDTH,
                                                                                height: THUMB_HEIGHT
                                                                            }, null, 8, ["pdf", "page"]),
                                                                            _createElementVNode("div", _hoisted_5, _toDisplayString(page) + " · " + _toDisplayString(formatMb(__props.perPageBytes[page - 1] ?? 0)) + " MB ", 1)
                                                                        ], 10, _hoisted_4),
                                                                        _createVNode(_component_v_btn, {
                                                                            icon: "mdi-magnify",
                                                                            size: "x-small",
                                                                            variant: "flat",
                                                                            color: "surface",
                                                                            class: "picker-cell__zoom",
                                                                            "aria-label": `Preview page ${page}`,
                                                                            onClick: _withModifiers(($event) => (previewPage.value = page), ["stop"])
                                                                        }, null, 8, ["aria-label", "onClick"])
                                                                    ]),
                                                                    _: 2
                                                                }, 1032, ["cols"]));
                                                            }), 128))
                                                        ]),
                                                        _: 2
                                                    }, 1024)
                                                ]),
                                                _: 1
                                            }, 8, ["items"])),
                                    (pdf.value && previewPage.value !== null)
                                        ? (_openBlock(), _createBlock(ChartPagePreviewDialog, {
                                            key: 3,
                                            "model-value": true,
                                            pdf: pdf.value,
                                            page: previewPage.value,
                                            "page-count": pageCount.value,
                                            selected: selected.value.has(previewPage.value),
                                            "size-bytes": __props.perPageBytes[previewPage.value - 1] ?? 0,
                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (!$event && (previewPage.value = null))),
                                            "onUpdate:page": _cache[3] || (_cache[3] = ($event) => (previewPage.value = $event)),
                                            onToggle: toggle
                                        }, null, 8, ["pdf", "page", "page-count", "selected", "size-bytes"]))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_actions, { class: "justify-end" }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_btn, {
                                        variant: "text",
                                        onClick: _cache[4] || (_cache[4] = ($event) => (emit('update:modelValue', false)))
                                    }, {
                                        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                                _createTextVNode("Cancel", -1)
                                            ]))]),
                                        _: 1
                                    }),
                                    _createVNode(_component_v_btn, {
                                        color: "primary",
                                        variant: "flat",
                                        disabled: !fits.value || selected.value.size === 0,
                                        onClick: confirm
                                    }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(" Use " + _toDisplayString(selected.value.size) + " " + _toDisplayString(selected.value.size === 1 ? "page" : "pages"), 1)
                                        ]),
                                        _: 1
                                    }, 8, ["disabled"])
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
